import { Component, type ReactNode } from 'react';

import { Sentry } from '@/util/sentry';

type ChatBubbleErrorBoundaryProps = {
  /**
   * Short identifier for the bubble variant, used as the `bubbleVariant`
   * Sentry tag so the rollout dashboard can isolate error rate per variant
   * (e.g. `'extracted-questions'`, `'extracted-statements'`).
   */
  variant: string;
  messageSid?: string;
  conversationId?: string;
  /**
   * Rendered when the wrapped subtree throws. Callers typically pass a plain
   * `ChatBubble` so the underlying message text stays in the conversation
   * even when the variant-specific UI fails.
   */
  fallback?: ReactNode;
  children: ReactNode;
};

type ChatBubbleErrorBoundaryState = {
  hasError: boolean;
};

/**
 * Local error boundary around a single chat bubble's variant-specific UI.
 * Two responsibilities:
 *
 *   1. Contain render errors so they don't bubble up to `ChatErrorBoundary`
 *      and take down the entire conversation.
 *   2. Tag the captured exception distinctly from generic chat-screen errors
 *      (`component: 'ChatBubbleErrorBoundary'`, `boundaryType: 'chat-bubble'`,
 *      plus a per-variant `bubbleVariant` tag) so error rate per bubble
 *      variant is filterable in Sentry.
 */
export class ChatBubbleErrorBoundary extends Component<
  ChatBubbleErrorBoundaryProps,
  ChatBubbleErrorBoundaryState
> {
  state: ChatBubbleErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ChatBubbleErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, {
      tags: {
        component: 'ChatBubbleErrorBoundary',
        boundaryType: 'chat-bubble',
        bubbleVariant: this.props.variant,
      },
      extra: {
        errorMessage: error?.message,
        errorName: error?.name,
        componentStack: errorInfo?.componentStack,
        messageSid: this.props.messageSid,
        conversationId: this.props.conversationId,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }

    return this.props.children;
  }
}
